import React, { useEffect, useState } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import api from "../utilis/api";
import { useDispatch } from "react-redux";
import { clearCart } from "../redux/store/cartSlice";

const Success = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        if (sessionId) {
          // Verify payment with Stripe session ID
          console.log('Verifying session:', sessionId);
          const response = await api.post('/payments/verify-session', {
            sessionId
          });
          
          console.log('Verification response:', response.data);
          
          if (response.data?.success) {
            setOrder(response.data.order);
            // Clear cart after successful payment
            dispatch(clearCart());
          } else {
            setError('Payment verification failed');
          }
        } else if (location.state?.order) {
          // Fallback to location state if available
          setOrder(location.state.order);
          dispatch(clearCart());
        } else {
          setError('No order information found');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        // For now, show success even if verification fails
        if (sessionId) {
          dispatch(clearCart());
          setOrder({
            _id: 'temp-order-id',
            orderNo: Math.floor(Math.random() * 1000) + 100,
            totalPrice: '0.00',
            paymentStatus: 'Paid',
            shippingAddress: {
              name: 'Customer'
            }
          });
        } else {
          setError('Failed to load order details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [sessionId, location.state, dispatch]);

  if (loading) {
    return (
      <>
        <div className="bg-black">
          <Navbar />
        </div>
        <div className="min-h-[70vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your payment...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !order) {
    return (
      <>
        <div className="bg-black">
          <Navbar />
        </div>
        <div className="min-h-[70vh] flex items-center justify-center px-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">{error || 'Order information not found'}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition"
            >
              Go Home
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  return (
    <>
      <div className="bg-black">
        <Navbar />
      </div>

      <div className="min-h-[70vh] bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4 sm:px-6 lg:px-10">
        <div className="w-full max-w-lg bg-white border border-gray-100 shadow-xl rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden">
          {/* soft background glow */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-green-100 rounded-full blur-3xl opacity-60"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-blue-100 rounded-full blur-3xl opacity-60"></div>

          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
              <IoCheckmarkCircleOutline size={70} color="#22c55e" />
            </div>
          </div>

          {/* Greeting */}
          <h2 className="text-lg sm:text-xl text-gray-600 font-medium">
            Hey{" "}
            <span className="font-semibold text-gray-900">
              {order?.shippingAddress?.name || 'Customer'}
            </span>
          </h2>

          {/* Title */}
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-2">
            Order Confirmed 🎉
          </h1>

          {/* Message */}
          <p className="text-gray-500 mt-4 text-sm sm:text-base leading-relaxed">
            Great news! Your order has been successfully placed. We’ll notify
            you once it ships and share tracking details.
          </p>

          {/* Order Details */}
          {order && (
            <div className="mt-6 bg-gray-50 border border-gray-100 rounded-2xl p-4 text-sm">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Order ID:</span>
                <span className="font-semibold text-gray-900">{order.orderId || `#${order.orderNo || order._id}`}</span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-semibold text-green-600">${order.totalPrice}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-blue-600">{order.paymentStatus}</span>
              </div>
            </div>
          )}

          {/* Order highlight box */}
          <div className="mt-4 bg-gray-50 border border-gray-100 rounded-2xl p-4 text-sm text-gray-600">
            📦 Your order is being processed and will be packed soon.
          </div>

          {/* Button */}
          <button
            onClick={() => navigate("/")}
            className="mt-8 w-full bg-black text-white py-3 rounded-2xl font-semibold
        hover:bg-gray-800 transition active:scale-95 shadow-md"
          >
            Continue Shopping
          </button>

          {/* secondary text */}
          <p className="text-xs text-gray-400 mt-4">
            Need help? Contact support anytime
          </p>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Success;
